import { StandardEvents } from '@shopify/events';

class RylSoldOut extends HTMLElement {
  #section;
  #handleProductSelect;
  #dismissButton;
  #restoreButton;
  #actions;
  #dismissed;
  #submitButton;

  connectedCallback() {
    this.#section = this.closest('.shopify-section');
    this.#dismissButton = this.querySelector('[data-ryl-soldout-dismiss]');
    this.#restoreButton = this.querySelector('[data-ryl-soldout-restore]');
    this.#actions = this.querySelector('[data-ryl-soldout-actions]');
    this.#dismissed = this.querySelector('[data-ryl-soldout-dismissed]');
    this.#submitButton = this.querySelector('[data-ryl-soldout-submit]');

    this.#dismissButton?.addEventListener('click', this.#dismiss);
    this.#restoreButton?.addEventListener('click', this.#restore);

    this.#handleProductSelect = (event) => {
      if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

      event.promise
        .then(({ detail }) => {
          if (!detail) return;
          if (detail.productId && String(detail.productId) !== String(this.dataset.productId) && !detail.newProduct) {
            return;
          }

          const resource = detail.resource;
          const newProduct = detail.newProduct;

          if (newProduct) {
            if (newProduct.id != null) this.dataset.productId = String(newProduct.id);
            if (newProduct.title) this.dataset.productTitle = String(newProduct.title);
            if (newProduct.url) this.dataset.productUrl = String(newProduct.url);
          }

          if (!resource) {
            this.hidden = true;
            return;
          }

          const soldOut = resource.available === false;
          this.dataset.variantId = resource.id != null ? String(resource.id) : '';
          this.dataset.variantTitle = resource.title || '';
          this.#syncHiddenFields();
          this.hidden = !soldOut;

          if (soldOut) {
            this.#restore({ focus: false });
          }
        })
        .catch((error) => {
          if (error?.name !== 'AbortError') console.warn('[ryl-sold-out] Variant update failed:', error);
        });
    };

    this.#section?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.#syncHiddenFields();
  }

  disconnectedCallback() {
    this.#dismissButton?.removeEventListener('click', this.#dismiss);
    this.#restoreButton?.removeEventListener('click', this.#restore);
    if (this.#handleProductSelect) {
      this.#section?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    }
  }

  #syncHiddenFields() {
    const productId = this.dataset.productId || '';
    const productTitle = this.dataset.productTitle || '';
    const productUrl = this.dataset.productUrl || '';
    const variantId = this.dataset.variantId || '';
    const variantTitle = this.dataset.variantTitle || '';

    this.querySelectorAll('[data-ryl-soldout-product-id]').forEach((input) => {
      input.value = productId;
    });
    this.querySelectorAll('[data-ryl-soldout-product-title]').forEach((input) => {
      input.value = productTitle;
    });
    this.querySelectorAll('[data-ryl-soldout-product-url]').forEach((input) => {
      const origin = window.location.origin;
      input.value = productUrl.startsWith('http') ? productUrl : `${origin}${productUrl}`;
    });
    this.querySelectorAll('[data-ryl-soldout-variant-id]').forEach((input) => {
      input.value = variantId;
    });
    this.querySelectorAll('[data-ryl-soldout-variant-title]').forEach((input) => {
      input.value = variantTitle;
    });

    if (this.#submitButton instanceof HTMLButtonElement) {
      this.#submitButton.disabled = !variantId;
    }
  }

  #dismiss = () => {
    if (this.#actions) this.#actions.hidden = true;
    if (this.#dismissed) this.#dismissed.hidden = false;
    if (this.#dismissButton instanceof HTMLButtonElement) {
      this.#dismissButton.hidden = true;
      this.#dismissButton.setAttribute('aria-expanded', 'false');
    }
  };

  #restore = (options = { focus: true }) => {
    if (this.#actions) this.#actions.hidden = false;
    if (this.#dismissed) this.#dismissed.hidden = true;
    if (this.#dismissButton instanceof HTMLButtonElement) {
      this.#dismissButton.hidden = false;
      this.#dismissButton.setAttribute('aria-expanded', 'true');
      if (options?.focus !== false) this.#dismissButton.focus({ preventScroll: true });
    }
  };
}

if (!customElements.get('ryl-sold-out')) {
  customElements.define('ryl-sold-out', RylSoldOut);
}
